import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  CONSENT_STATES,
  CUSTOMER_BASES,
  CUSTOMER_SORTS,
  CUSTOMER_TYPES,
  DEFAULT_CUSTOMER_BASIS,
  DEFAULT_CUSTOMER_SORT,
  DEFAULT_SORT_ORDER,
  SORT_ORDERS,
  type ConsentState,
  type CustomerBasis,
  type CustomerSort,
  type CustomersQuery,
  type CustomerType,
  type SortOrder,
} from '@/api/customers'
import { DEFAULT_FILTERS, type GlobalFilters } from '@/lib/filters'

/*
 * The page's own URL vocabulary: who is listed, the record filters, sort and order,
 * read, validated and written beside the global filters, and the list query they make.
 * Never a search term: what a search looks for stays in the page's memory.
 *
 * The customer type is the filter bar's `customer_type` dimension, so the Overview's
 * New and Returning customers tiles (`?customer_type=new|returning`) drill straight
 * into the customers they counted, and the page's own chips write the same parameter.
 */

const BASIS_PARAM = 'basis'
const MEMBER_PARAM = 'member'
const VIP_PARAM = 'vip'
const CONSENT_PARAM = 'consent'
const SORT_PARAM = 'sort'
const ORDER_PARAM = 'order'

const FLAGS = ['true', 'false'] as const

function oneOf<T extends string>(values: readonly T[], value: string | null): value is T {
  return value !== null && (values as readonly string[]).includes(value)
}

function flag(value: string | null): boolean | undefined {
  return oneOf(FLAGS, value) ? value === 'true' : undefined
}

/** The page's parameters a link carries that the page cannot use: left in place, they would only mislead. */
function unusable(params: URLSearchParams): string[] {
  const stale: string[] = []
  const check = (param: string, values: readonly string[]) => {
    if (params.getAll(param).some((value) => !values.includes(value))) stale.push(param)
  }
  check(BASIS_PARAM, CUSTOMER_BASES)
  check(MEMBER_PARAM, FLAGS)
  check(VIP_PARAM, FLAGS)
  check(CONSENT_PARAM, CONSENT_STATES)
  check(SORT_PARAM, CUSTOMER_SORTS)
  check(ORDER_PARAM, SORT_ORDERS)
  return stale
}

export interface CustomerParams {
  basis: CustomerBasis
  /** The filter bar's `customer_type`, when it is one the API takes. */
  customerType: CustomerType | undefined
  member: boolean | undefined
  vip: boolean | undefined
  consent: ConsentState[]
  sort: CustomerSort
  order: SortOrder
  setBasis: (basis: CustomerBasis) => void
  setCustomerType: (type: CustomerType | undefined) => void
  setMember: (member: boolean | undefined) => void
  setVip: (vip: boolean | undefined) => void
  toggleConsent: (state: ConsentState) => void
  /** `null`, or a key the API does not take, restores the default order. */
  setSort: (sort: { sort: string; order: SortOrder } | null) => void
  /** Clears the page's own filters and the customer type; who is listed stays. */
  clearFilters: () => void
}

/** The page's filters and sort, kept in the URL beside the global filters so a link reproduces the view. Defaults are left out. */
export function useCustomerParams(filters: GlobalFilters): CustomerParams {
  const [params, setParams] = useSearchParams()

  // A value the page cannot use (a mistyped sort, `vip=maybe`) is dropped from the URL, so the link shows what the page shows.
  useEffect(() => {
    const stale = unusable(params)
    if (stale.length === 0) return
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        for (const param of stale) next.delete(param)
        return next
      },
      { replace: true },
    )
  }, [params, setParams])

  const update = (change: (next: URLSearchParams) => void) =>
    setParams(
      (current) => {
        const next = new URLSearchParams(current)
        change(next)
        return next
      },
      { replace: true },
    )
  const setOrDelete = (next: URLSearchParams, param: string, value: string | undefined) => {
    if (value === undefined) next.delete(param)
    else next.set(param, value)
  }

  const basis = params.get(BASIS_PARAM)
  const sort = params.get(SORT_PARAM)
  const order = params.get(ORDER_PARAM)
  const type = filters.dimensions.customer_type ?? null
  const consent = params.getAll(CONSENT_PARAM).filter((value): value is ConsentState => oneOf(CONSENT_STATES, value))
  return {
    basis: oneOf(CUSTOMER_BASES, basis) ? basis : DEFAULT_CUSTOMER_BASIS,
    customerType: oneOf(CUSTOMER_TYPES, type) ? type : undefined,
    member: flag(params.get(MEMBER_PARAM)),
    vip: flag(params.get(VIP_PARAM)),
    consent: CONSENT_STATES.filter((state) => consent.includes(state)),
    sort: oneOf(CUSTOMER_SORTS, sort) ? sort : DEFAULT_CUSTOMER_SORT,
    order: oneOf(SORT_ORDERS, order) ? order : DEFAULT_SORT_ORDER,
    setBasis: (next) => update((url) => setOrDelete(url, BASIS_PARAM, next === DEFAULT_CUSTOMER_BASIS ? undefined : next)),
    setCustomerType: (next) => update((url) => setOrDelete(url, 'customer_type', next)),
    setMember: (next) => update((url) => setOrDelete(url, MEMBER_PARAM, next === undefined ? undefined : String(next))),
    setVip: (next) => update((url) => setOrDelete(url, VIP_PARAM, next === undefined ? undefined : String(next))),
    toggleConsent: (state) =>
      update((url) => {
        const chosen = new Set(url.getAll(CONSENT_PARAM))
        if (chosen.has(state)) chosen.delete(state)
        else chosen.add(state)
        url.delete(CONSENT_PARAM)
        for (const value of CONSENT_STATES) if (chosen.has(value)) url.append(CONSENT_PARAM, value)
      }),
    setSort: (choice) =>
      update((url) => {
        const isDefault =
          !choice ||
          !oneOf(CUSTOMER_SORTS, choice.sort) ||
          (choice.sort === DEFAULT_CUSTOMER_SORT && choice.order === DEFAULT_SORT_ORDER)
        setOrDelete(url, SORT_PARAM, isDefault ? undefined : choice.sort)
        setOrDelete(url, ORDER_PARAM, isDefault ? undefined : choice.order)
      }),
    clearFilters: () =>
      update((url) => {
        for (const param of ['customer_type', MEMBER_PARAM, VIP_PARAM, CONSENT_PARAM]) url.delete(param)
      }),
  }
}

/**
 * The list's query, less its page, and its `scope`: the same less its sort, which is
 * what a search looks within. The global filters enter as the part the customer routes
 * read (the period, the website and the environments; never the comparison or another
 * dimension, and the customer type is read on its own). Each object is the same until
 * a field in it changes, so a search, which is a new audit record each time it runs,
 * runs again only when its scope changes: never for a new order, another comparison, a
 * channel or a page parameter.
 */
export function useCustomersQuery(
  filters: GlobalFilters,
  params: CustomerParams,
): { scope: CustomersQuery; query: CustomersQuery } {
  const { basis, customerType, member, vip, sort, order } = params
  const { range, from, to } = filters
  const website = filters.dimensions.website
  // A new URL parses into new arrays of the same values: the same values keep the same array.
  const environments = useStableArray(filters.environments)
  const consent = useStableArray(params.consent)
  const scope = useMemo(
    (): CustomersQuery => ({
      filters: { ...DEFAULT_FILTERS, range, from, to, environments: [...environments], dimensions: website === undefined ? {} : { website } },
      basis,
      customerType,
      member,
      vip,
      consent,
    }),
    [range, from, to, environments, website, basis, customerType, member, vip, consent],
  )
  const query = useMemo((): CustomersQuery => ({ ...scope, sort, order }), [scope, sort, order])
  return { scope, query }
}

/** `values`, or the array this hook last returned when it holds the same values in the same order. */
function useStableArray<T>(values: readonly T[]): readonly T[] {
  const [stable, setStable] = useState(values)
  const same = stable.length === values.length && stable.every((value, index) => value === values[index])
  if (!same) setStable(values)
  return same ? stable : values
}
